import { describeDuration } from '@bartizo/shared';
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { addToBlocklist } from '../api/blocklist.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * Putting an address on the fleet blocklist (P11.6): from the Blocklist page, or
 * from the address's own page with it filled in (P11.7).
 */

const errorText = (caught: unknown, fallback: string): string =>
  caught instanceof Error ? caught.message : fallback;

export function BlocklistAddPanel({
  ladder,
  initialIp = '',
  onDone,
  onClose,
}: {
  ladder: readonly number[];
  /** The address page opens it for its own address. */
  initialIp?: string;
  onDone: () => void;
  onClose: () => void;
}): ReactNode {
  const [ip, setIp] = useState(initialIp);
  const [reason, setReason] = useState('');
  const [seconds, setSeconds] = useState(String(ladder[0] ?? 14_400));
  const add = useMutation({
    mutationFn: () =>
      addToBlocklist({ ip: ip.trim(), reason: reason.trim(), duration_seconds: Number(seconds) }),
    onSuccess: onDone,
  });
  const choices = [...new Set([...ladder, 365 * 24 * 3600])];

  return (
    <Dialog
      variant="panel"
      eyebrow="Fleet blocklist"
      title="Add an address"
      description="Every server keeps it out within the hour. The same checks as a ban apply: never the API, a proxy in front of a server, or an allowlisted address."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={ip.trim() === '' || reason.trim() === ''}
            busy={add.isPending}
            busyLabel="Adding…"
            onClick={() => {
              add.mutate();
            }}
          >
            Add to every server
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <TextField
          label="Address"
          placeholder="198.51.100.7"
          autoFocus
          value={ip}
          onChange={(event) => {
            setIp(event.target.value.trim());
          }}
        />
        <SelectField
          label="For"
          value={seconds}
          onChange={(event) => {
            setSeconds(event.target.value);
          }}
        >
          {choices.map((each) => (
            <option key={each} value={String(each)}>
              {describeDuration(each)}
            </option>
          ))}
        </SelectField>
        <div className="span-all">
          <TextField
            label="Why"
            hint="Shown on the list and kept in the audit log."
            maxLength={300}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </div>
      </div>
      {add.isError && <Alert tone="danger">{errorText(add.error, 'Could not add it.')}</Alert>}
    </Dialog>
  );
}
